import $ from 'jquery';

import Autocomplete from 'chairisher/component/autocomplete';
import Pill, { PillAddOnType } from 'chairisher/component/pill';
import StorageUtils from 'chairisher/util/storage';

/**
 * Displays a static set of multi-level choices and queries a given endpoint,
 * as well as presents the available choices via a simple dropdown menu.
 *
 * @param {Object} settings Object of settings used to customize the Autocomplete instance
 * @param {jQuery} settings.$queryEl jQuery element where queries can be input
 * @param {boolean=} settings.areOnlyLeafNodesSelectable Flag that indicates if all nodes are selectable or just leaf nodes.
 * @param {string=} settings.dataDisplayKey The key found in a choice whose value should be displayed in the query field upon selection
 * @param {string=} settings.dataEndpoint The endpoint on the server that returns autocomplete choices
 * @param {boolean=} settings.isMultiSelect Flag that indicates if the choices are multi-select or not
 * @param {boolean=} settings.isSingleSelect Flag that indicates if the choices are single select or not (used for primary_color_code)
 * @param {string=} settings.menuChoiceContentTemplateSelector Selector of the element that contains the template to use when marshaling menu choices
 * @param {boolean=} settings.menuChoiceHasThumbnail Flag that indicates if menu choices should expect to display a thumbnail image or not
 * @param {number=} settings.minQueryLength Default length of queryString required before a query can be attempted
 * @param {responseFilterCallback=} settings.responseFilterCallback Filters a set of choices returned from the server.
 * @param {boolean=} settings.shouldPersistOnChange Flag indicating if this component's data should be saved to local storage on change
 * @param {string=} settings.storageKey Key to use when storing this component's data in local storage
 */
export default class ChoiceTreeAutocomplete {
    constructor(settings) {
        const options = $.extend(
            {
                $queryEl: null,
                areOnlyLeafNodesSelectable: false,
                dataDisplayKey: 'display',
                dataEndpoint: null,
                isMultiSelect: true,
                isSingleSelect: false,
                menuChoiceContentTemplateSelector: '#js-template-menu-choice-checkbox',
                menuChoiceHasThumbnail: true,
                minQueryLength: 0,
                responseFilterCallback: this.responseFilterCallback.bind(this),
                shouldPersistOnChange: false,
                storageKey: null,
            },
            settings,
        );

        // Instance of an Autocomplete component to manipulate
        this.autocomplete = new Autocomplete(options);

        this.$queryEl = this.autocomplete.getQueryEl();
        this.areOnlyLeafNodesSelectable = options.areOnlyLeafNodesSelectable;
        this.dataDisplayKey = options.dataDisplayKey;
        this.dataEndpoint = options.dataEndpoint;
        this.isMultiSelect = options.isMultiSelect;
        this.isSingleSelect = options.isSingleSelect;
        this.menuChoiceContentTemplateSelector = options.menuChoiceContentTemplateSelector;
        this.menuChoiceHasThumbnail = options.menuChoiceHasThumbnail;
        this.minQueryLength = options.minQueryLength;
        this.shouldPersistOnChange = options.shouldPersistOnChange;
        this.storageKey = options.storageKey;

        // A structural element to contain Choices stored as Pills
        this.$pillContainerEl = $('<div></div>', { class: 'pill-container js-pill-container' });

        // The jQuery element that wraps this component
        this.$componentWrapper = this.$queryEl.closest('.js-autocomplete-input');

        // A map of choice values to choice objects
        this.valueKeyToValueMap = {};

        this.initialize();
        this.bindAutocomplete();
        this.bindMenu();
    }

    /**
     * Adds a Choice object to the internal datastructure and adds a Pill to the UI to represent it
     *
     * @param {Choice} choice The choice object to add
     * @param {boolean=} shouldToggleChoiceInput Passing in false prevents toggling, otherwise everything else toggles
     */
    addChoice(choice, shouldToggleChoiceInput) {
        const key = choice.value;
        this.valueKeyToValueMap[key] = choice;

        const pill = new Pill({
            dataAddOnType:
                $.inArray(this.$queryEl.attr('name'), ['colors', 'primary_color_code']) !== -1
                    ? PillAddOnType.SWATCH
                    : null,
            dataDisplayKey: this.dataDisplayKey,
            value: choice,
        });
        pill.bind();
        this.bindPill(pill);

        this.$pillContainerEl.append(pill.getEl());
        if (shouldToggleChoiceInput !== false) {
            this.toggleChoiceInput(key, true);
        }
    }

    /**
     * Disables the Autocompleter from receiving user input
     */
    disable() {
        this.autocomplete.disable();
        this.$pillContainerEl.find('.js-pill').addClass('disabled');
    }

    /**
     * Enables the Autocompleter, allowing it to receive user input
     */
    enable() {
        this.autocomplete.enable();
        this.$pillContainerEl.find('.js-pill').removeClass('disabled');
    }

    /**
     * @param {string} key The key to look up
     * @returns {boolean} True indicates a choice is already stored by the given key
     */
    hasChoice(key) {
        return key in this.valueKeyToValueMap;
    }

    /**
     * @returns {boolean} True indicates the Autocompleter is enabled; false otherwise
     */
    areQueriesEnabled() {
        return this.autocomplete.areQueriesEnabled();
    }

    /**
     * @returns {Menu} The Menu instance attached directly to this component
     */
    getMenu() {
        return this.autocomplete.getMenu();
    }

    /**
     * @returns {jQuery} The Autocompleter element that accepts user input
     */
    getQueryEl() {
        return this.autocomplete.getQueryEl();
    }

    /**
     * @returns {Array.<string>|string} Array of values currently stored by this component or string if single select
     */
    getValue() {
        const vals = Object.keys(this.valueKeyToValueMap);
        if (this.isSingleSelect) {
            return vals.length ? vals[0] : ''; // Make sure we return a string if the choicetree is single select
        }
        return vals;
    }

    /**
     * Persists the current selections in local storage
     */
    persistLocally() {
        if (this.storageKey) {
            StorageUtils.setItem(this.storageKey, JSON.stringify(this.getValueChoices()));
        }
    }

    /**
     * Removes all choices from the component
     */
    removeAllChoices() {
        this.valueKeyToValueMap = {};
        this.$pillContainerEl.html('');
        this.getMenu().getMenuEl().find('input:checked').prop('checked', false);
    }

    /**
     * Removes a Choice object from the internal datastructure and removes its corresponding Pill from the ui
     *
     * @param {string} key The key representing the Choice object to remove
     * @param {boolean=} shouldToggleChoiceInput Passing in false prevents toggling, otherwise everything else toggles
     */
    removeChoice(key, shouldToggleChoiceInput) {
        delete this.valueKeyToValueMap[key];

        this.$pillContainerEl
            .find('.js-pill')
            .filter((_, element) => $(element).attr('data-value') === key)
            .remove();

        if (shouldToggleChoiceInput !== false) {
            this.toggleChoiceInput(key, false);
        }
    }

    /**
     * Updates the selected choices with those currently stored in local storage
     */
    updateFromLocalStorage() {
        if (this.storageKey) {
            const valJson = StorageUtils.getItem(this.storageKey);
            if (valJson) {
                const vals = JSON.parse(valJson);
                for (let i = 0; i < vals.length; i += 1) {
                    this.addChoice(vals[i], true);
                }
            }
        }
    }

    /**
     * Binds actions to the various elements that comprise the ChoiceTreeAutocompleter
     */
    bindAutocomplete() {
        const $queryEl = this.autocomplete.getQueryEl();

        $queryEl.on('focus blur', ({ type: eventType }) => {
            const isFocus = eventType === 'focus';
            this.$componentWrapper.closest('.js-form-group').toggleClass('has-focus', isFocus);

            const menu = this.autocomplete.getMenu();
            menu.toggle(isFocus);

            if (!isFocus) {
                menu.updateSuggestionChoices([]);

                this.autocomplete.setQueryText('');
                this.autocomplete.showPlaceholder();
            }
        });

        $queryEl.on('autocomplete:selected', (e, choice) => {
            e.preventDefault();

            if (this.isSingleSelect) {
                this.removeAllChoices();
            }
            this.addChoice(choice);

            this.autocomplete.setQueryText('');
            this.autocomplete.showPlaceholder();

            this.triggerChange();
        });
    }

    /**
     * Binds actions to the various elements that comprise the Pill
     *
     * @param {Pill} pill The pill whose actions should be bound
     */
    bindPill(pill) {
        pill.getEl().on('pill:remove', (e, choice) => {
            if (this.areQueriesEnabled()) {
                e.preventDefault();
                this.removeChoice(choice.value);
                this.persistLocally();
                this.triggerChange();
            }
        });
    }

    /**
     * Binds actions to the various elements that comprise the Menu
     */
    bindMenu() {
        const menu = this.autocomplete.getMenu();
        const $menuEl = menu.getMenuEl();

        const toggleChoice = (choice, shouldToggle) => {
            if (this.hasChoice(choice.value)) {
                this.removeChoice(choice.value, !!shouldToggle);
            } else {
                this.addChoice(choice, !!shouldToggle);
                this.autocomplete.setQueryText('');
                this.autocomplete.showPlaceholder();
            }
        };

        $menuEl.off('input:selected').on('input:selected', (e, choice) => {
            e.preventDefault();

            if (this.isSingleSelect) {
                this.removeAllChoices();
            }

            toggleChoice(choice);

            if (choice.submenu) {
                const hasValue = this.hasChoice(choice.value);
                choice.submenu.toggle(hasValue);

                if (!hasValue && choice.children) {
                    for (let i = 0; i < choice.children.length; i += 1) {
                        const child = choice.children[i];
                        this.removeChoice(child.value, true);
                    }
                }
            }

            if (this.shouldPersistOnChange) {
                this.persistLocally();
            }

            this.triggerChange();
        });

        $menuEl.off('menuchoice:selected').on('menuchoice:selected', (e, choice) => {
            e.preventDefault();

            if (!choice.children) {
                toggleChoice(choice, true);

                if (this.shouldPersistOnChange) {
                    this.persistLocally();
                }
            }
            this.triggerChange();
        });
    }

    /**
     * @returns {Array.<Choice>} Collection of choices currently selected
     */
    getValueChoices() {
        return Object.values(this.valueKeyToValueMap);
    }

    /**
     * Modifies the DOM to suit the component's needs...
     */
    initialize() {
        this.$componentWrapper.addClass('choicetree'); // todo: (CHAIR-5561) circle back and make this less brittle...
        this.$componentWrapper.after(this.$pillContainerEl);
    }

    /**
     * Filters a set of choices returned from the server.
     *
     * @param {Array.<Choice>} choices The Choices to filter
     * @returns {Array.<Choice>} The filtered Choices
     */
    responseFilterCallback(choices) {
        const filteredChoices = [];

        if (Array.isArray(choices)) {
            for (let i = 0; i < choices.length; i += 1) {
                const choice = choices[i];

                if (!this.hasChoice(choice.value)) {
                    filteredChoices.push(choice);
                }
            }
        }

        return filteredChoices;
    }

    /**
     * Checks or unchecks the input for a given key
     *
     * @param {string} key The key representing the name of the checkbox to check or uncheck
     * @param {boolean} isChecked True indicates the input should be checked, false otherwise
     */
    toggleChoiceInput(key, isChecked) {
        this.getMenu()
            .getMenuEl()
            .find(`input[value=${JSON.stringify(key)}]`)
            .prop('checked', isChecked);
    }

    /**
     * Triggers the `choicetree:change` event and passes the current value to clients
     */
    triggerChange() {
        this.$queryEl.trigger('choicetree:change', [this.getValue(), this]);
    }
}
